<script setup lang="ts">
import { adminSupplierInvoiceService } from '@/services/admin/admin-supplier-invoice-service.ts';
import {
  PhCalendarBlank,
  PhFile,
  PhListChecks,
  PhPercent,
  PhTag,
} from '@phosphor-icons/vue';
import { utils, type models } from 'cesg-shared';
import { SupplierInvoiceStatusType, VatType } from 'cesg-shared/dist/models';
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';

type ExportStatus = SupplierInvoiceStatusType | 'verified-debt';
type FilterStatus = ExportStatus | 'all';

const { t } = useI18n();

const props = defineProps<{
  activeFilter?: FilterStatus;
}>();

const emit = defineEmits<{
  close: [];
}>();

const STATUS_OPTIONS: { value: ExportStatus; labelKey: string }[] = [
  {
    value: SupplierInvoiceStatusType.UnderCertificate,
    labelKey: 'undercertificate',
  },
  { value: SupplierInvoiceStatusType.Parked, labelKey: 'parked' },
  { value: SupplierInvoiceStatusType.Disputed, labelKey: 'disputed' },
  { value: SupplierInvoiceStatusType.Voided, labelKey: 'voided' },
  { value: SupplierInvoiceStatusType.Approved, labelKey: 'approved' },
  { value: 'verified-debt', labelKey: 'verified-debt' },
  { value: SupplierInvoiceStatusType.Paid, labelKey: 'verified-payed' },
];

const selectedStatuses = ref<Set<ExportStatus>>(
  props.activeFilter &&
    STATUS_OPTIONS.some((opt) => opt.value === props.activeFilter)
    ? new Set<ExportStatus>([props.activeFilter as ExportStatus])
    : new Set<ExportStatus>(STATUS_OPTIONS.map((opt) => opt.value)),
);

const VAT_TYPE_OPTIONS: VatType[] = [
  VatType.Normal,
  VatType.Reverse,
  VatType.EuInternal,
  VatType.None,
];

const selectedVatTypes = ref<Set<VatType>>(new Set<VatType>(VAT_TYPE_OPTIONS));

const toggleVatType = (value: VatType) => {
  if (selectedVatTypes.value.has(value)) {
    selectedVatTypes.value.delete(value);
  } else {
    selectedVatTypes.value.add(value);
  }
};

const FIELD_OPTIONS = [
  'serialNumber',
  'invoiceNumber',
  'ocr',
  'supplierName',
  'projectName',
  'projectNumber',
  'accounts',
  'status',
  'invoiceDate',
  'dueDate',
  'finalPayDate',
  'totalAmount',
  'vat',
  'booked',
  'approver',
  'paymentFileId',
] as const;

type ExportField = (typeof FIELD_OPTIONS)[number];

const selectedFields = ref<Set<ExportField>>(
  new Set<ExportField>(FIELD_OPTIONS),
);

const allFieldsSelected = computed(
  () => selectedFields.value.size === FIELD_OPTIONS.length,
);

const toggleField = (value: ExportField) => {
  if (selectedFields.value.has(value)) {
    selectedFields.value.delete(value);
  } else {
    selectedFields.value.add(value);
  }
};

const toggleAllFields = () => {
  selectedFields.value = allFieldsSelected.value
    ? new Set<ExportField>()
    : new Set<ExportField>(FIELD_OPTIONS);
};

const invoiceDateFrom = ref('');
const invoiceDateTo = ref('');

const isExporting = ref(false);
const errorMsg = ref('');
const noMatches = ref(false);

const today = new Date().toISOString().split('T')[0];

const INVALID_FILENAME_CHARS = /[\\/:*?"<>| \x00-\x1f]/g;

const sanitizeFileName = (value: string) =>
  value.replace(INVALID_FILENAME_CHARS, '');

const rawCustomFileName = ref('');

const customFileName = computed({
  get: () => rawCustomFileName.value,
  set: (value: string) => {
    rawCustomFileName.value = sanitizeFileName(value);
  },
});

const defaultFileName = computed(() => {
  const statuses = [...selectedStatuses.value];
  const prefix = t('adminSupplierInvoice.export.defaultFileNamePrefix');
  const statusPart = statuses.length === 1 ? `-${statuses[0]}` : '';

  const from = invoiceDateFrom.value;
  const to = invoiceDateTo.value;
  let datePart = today;
  if (from && to) {
    datePart = `${from}_${to}`;
  } else if (from || to) {
    datePart = from || to;
  }

  return `${prefix}${statusPart}-${datePart}`;
});

const fileName = computed(() => {
  const custom = customFileName.value.trim().replace(/\.+$/, '');
  const base = custom || defaultFileName.value;
  return base.toLowerCase().endsWith('.csv') ? base : `${base}.csv`;
});

const toggleStatus = (value: ExportStatus) => {
  if (selectedStatuses.value.has(value)) {
    selectedStatuses.value.delete(value);
  } else {
    selectedStatuses.value.add(value);
  }
};

const handleExport = async () => {
  errorMsg.value = '';
  noMatches.value = false;
  isExporting.value = true;

  try {
    const statuses = [...selectedStatuses.value];
    const filters: models.ExportSupplierInvoicesQuery = {
      status: statuses.length ? statuses : undefined,
      invoiceDateFrom: invoiceDateFrom.value || undefined,
      invoiceDateTo: invoiceDateTo.value || undefined,
      vatType:
        selectedVatTypes.value.size < VAT_TYPE_OPTIONS.length
          ? [...selectedVatTypes.value]
          : undefined,
      fields:
        selectedFields.value.size < FIELD_OPTIONS.length
          ? [...selectedFields.value]
          : undefined,
    };

    const csv = await adminSupplierInvoiceService.exportToCsv(filters);

    const rowCount = csv
      .trim()
      .split('\n')
      .filter((line) => line.trim()).length;
    if (rowCount <= 1) {
      noMatches.value = true;
      return;
    }

    utils.downloadFile(new Blob([csv], { type: 'text/csv' }), fileName.value);
  } catch (error) {
    console.error('Failed to export supplier invoices CSV %o', error);
    errorMsg.value = t('adminSupplierInvoice.export.error');
  } finally {
    isExporting.value = false;
  }
};
</script>

<template>
  <Teleport to="body">
    <div class="modal is-active" role="dialog" aria-modal="true">
      <div class="modal-background" @click="emit('close')"></div>

      <div class="modal-card export-modal-card">
        <header class="modal-card-head">
          <p class="modal-card-title">
            {{ t('adminSupplierInvoice.export.title') }}
          </p>
          <button
            class="delete"
            aria-label="close"
            type="button"
            @click="emit('close')"
          ></button>
        </header>

        <section class="modal-card-body">
          <div class="export-columns">
            <div class="export-col">
              <div class="filename-field">
                <PhFile :size="18" weight="bold" />
                <input
                  class="filename-input"
                  type="text"
                  v-model="customFileName"
                  :placeholder="defaultFileName"
                  :aria-label="t('adminSupplierInvoice.export.fileNameLabel')"
                />
                <span class="filename-extension">.csv</span>
              </div>

              <div class="filter-cards">
                <div class="filter-card">
                  <div class="filter-card-header">
                    <PhTag :size="18" weight="bold" />
                    <span class="filter-card-title">{{
                      t('adminSupplierInvoice.export.statusLabel')
                    }}</span>
                  </div>
                  <div class="field">
                    <div class="status-chips">
                      <button
                        v-for="opt in STATUS_OPTIONS"
                        :key="opt.value"
                        type="button"
                        class="status-chip"
                        :class="[
                          opt.value,
                          { active: selectedStatuses.has(opt.value) },
                        ]"
                        :aria-pressed="selectedStatuses.has(opt.value)"
                        @click="toggleStatus(opt.value)"
                      >
                        <span class="status-chip-label">
                          {{ t(`adminSupplierInvoice.status.${opt.labelKey}`) }}
                        </span>
                        <span class="status-chip-check" aria-hidden="true">
                          <svg
                            v-if="selectedStatuses.has(opt.value)"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            stroke-width="3"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                          >
                            <path d="M20 6 9 17l-5-5" />
                          </svg>
                        </span>
                      </button>
                    </div>
                  </div>
                </div>

                <div class="filter-card">
                  <div class="filter-card-header">
                    <PhPercent :size="18" weight="bold" />
                    <span class="filter-card-title">{{
                      t('adminSupplierInvoice.export.vatTypeLabel')
                    }}</span>
                  </div>
                  <div class="field">
                    <div class="status-chips">
                      <button
                        v-for="vatType in VAT_TYPE_OPTIONS"
                        :key="vatType"
                        type="button"
                        class="vat-type-chip"
                        :class="{ active: selectedVatTypes.has(vatType) }"
                        :aria-pressed="selectedVatTypes.has(vatType)"
                        @click="toggleVatType(vatType)"
                      >
                        <span class="status-chip-label">
                          {{ t(`invoices.details.vatTypes.${vatType}`) }}
                        </span>
                        <span class="status-chip-check" aria-hidden="true">
                          <svg
                            v-if="selectedVatTypes.has(vatType)"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            stroke-width="3"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                          >
                            <path d="M20 6 9 17l-5-5" />
                          </svg>
                        </span>
                      </button>
                    </div>
                  </div>
                </div>

                <div class="filter-card">
                  <div class="filter-card-header">
                    <PhCalendarBlank :size="18" weight="bold" />
                    <span class="filter-card-title">{{
                      t('adminSupplierInvoice.export.periodTitle')
                    }}</span>
                  </div>
                  <div class="date-range">
                    <div class="field">
                      <label class="label">{{
                        t('adminSupplierInvoice.export.dateFromLabel')
                      }}</label>
                      <div class="control">
                        <input
                          class="input"
                          type="date"
                          v-model="invoiceDateFrom"
                        />
                      </div>
                    </div>
                    <div class="field">
                      <label class="label">{{
                        t('adminSupplierInvoice.export.dateToLabel')
                      }}</label>
                      <div class="control">
                        <input
                          class="input"
                          type="date"
                          v-model="invoiceDateTo"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div class="export-col">
              <div class="fields-field">
                <div class="fields-header">
                  <div class="fields-title">
                    <PhListChecks :size="18" weight="bold" />
                    <span class="filter-card-title">{{
                      t('adminSupplierInvoice.export.fieldsLabel')
                    }}</span>
                  </div>
                  <button
                    type="button"
                    class="select-all-button"
                    @click="toggleAllFields"
                  >
                    {{
                      allFieldsSelected
                        ? t('adminSupplierInvoice.export.deselectAll')
                        : t('adminSupplierInvoice.export.selectAll')
                    }}
                  </button>
                </div>
                <div class="fields-pills">
                  <button
                    v-for="fieldKey in FIELD_OPTIONS"
                    :key="fieldKey"
                    type="button"
                    class="field-pill"
                    :class="{ active: selectedFields.has(fieldKey) }"
                    :aria-pressed="selectedFields.has(fieldKey)"
                    @click="toggleField(fieldKey)"
                  >
                    <span class="field-pill-label">{{
                      t(`adminSupplierInvoice.export.fields.${fieldKey}`)
                    }}</span>
                    <span class="field-pill-check" aria-hidden="true">
                      <svg
                        v-if="selectedFields.has(fieldKey)"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        stroke-width="3"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      >
                        <path d="M20 6 9 17l-5-5" />
                      </svg>
                    </span>
                  </button>
                </div>
              </div>
            </div>
          </div>

          <p v-if="noMatches" class="help message-no-matches">
            {{ t('adminSupplierInvoice.export.noMatches') }}
          </p>
          <p v-if="errorMsg" class="help message-error">{{ errorMsg }}</p>
        </section>

        <footer class="modal-card-foot actions">
          <button type="button" class="button is-light" @click="emit('close')">
            {{ t('adminSupplierInvoice.export.closeButton') }}
          </button>
          <button
            type="button"
            class="button is-primary"
            :disabled="isExporting"
            @click="handleExport"
          >
            <span v-if="isExporting" class="export-spinner"></span>
            {{
              isExporting
                ? t('adminSupplierInvoice.export.exporting')
                : t('adminSupplierInvoice.export.exportButton')
            }}
          </button>
        </footer>
      </div>
    </div>
  </Teleport>
</template>

<style scoped lang="scss">
.export-modal-card {
  width: min(880px, 94vw);
  max-height: calc(100dvh - 4rem);
  display: flex;
  flex-direction: column;
}

.modal-card-head {
  padding-top: 1.5rem;
  padding-bottom: 0.85rem;

  .delete {
    height: 1.75rem;
    width: 1.75rem;
    max-height: 1.75rem;
    max-width: 1.75rem;
    min-height: 1.75rem;
    min-width: 1.75rem;
  }
}

.modal-card-body {
  position: relative;
  z-index: 2;
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.export-columns {
  display: flex;
  gap: 2rem;
  align-items: stretch;
  flex: 1;
  min-height: 0;
}

.export-col {
  flex: 1 1 0;
  min-width: 0;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.filename-field {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.85rem;
  padding-bottom: 0.6rem;
  border-bottom: 1px solid #e2e5ec;
  color: var(--primary-color, #001c74);
}

.filename-input {
  flex: 1;
  min-width: 0;
  border: none;
  border-radius: 0;
  background: transparent;
  padding: 0;
  font-size: 0.95rem;
  font-weight: 700;
  line-height: 1.2;
  color: #1f2937;
  outline: none;

  &::placeholder {
    color: #9aa1ae;
    font-weight: 600;
  }
}

.filename-extension {
  flex-shrink: 0;
  font-size: 0.9rem;
  font-weight: 600;
  line-height: 1.2;
  color: var(--primary-color, #001c74);
}

.filter-cards {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
}

.filter-card {
  border: 1px solid #e2e5ec;
  border-radius: 10px;
  padding: 1rem;
  background: #fff;
  box-shadow: 0 1px 3px rgba(15, 23, 42, 0.08);

  & + .filter-card {
    margin-top: 1.5rem;
  }

  .field:last-child {
    margin-bottom: 0;
  }

  .date-range {
    margin-top: 0;
  }
}

.filter-card-header {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.85rem;
  padding-bottom: 0.6rem;
  border-bottom: 1px solid #e2e5ec;
  color: var(--primary-color, #001c74);
}

.filter-card-title {
  font-size: 0.95rem;
  font-weight: 700;
  line-height: 1.2;
  color: #1f2937;
}

.fields-field {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
}

.fields-header {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  margin-bottom: 0.85rem;
  padding-bottom: 0.6rem;
  border-bottom: 1px solid #e2e5ec;
}

.fields-title {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: var(--primary-color, #001c74);
}

.select-all-button {
  border: none;
  background: none;
  padding: 0;
  font-size: 0.85rem;
  font-weight: 600;
  color: var(--primary-color, #001c74);
  cursor: pointer;

  &:hover {
    text-decoration: underline;
  }
}

.fields-pills {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 0.5rem;
  flex: 1;
  min-height: 0;
  overflow-y: auto;
}

.field-pill {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  border: 1px solid #cfd3dc;
  border-radius: 999px;
  padding: 0.38rem 0.55rem 0.38rem 0.85rem;
  font-size: 0.9rem;
  line-height: 1.2;
  font-weight: 600;
  background: #fff;
  color: #374151;
  cursor: pointer;
  transition:
    background-color 0.15s ease,
    border-color 0.15s ease,
    color 0.15s ease;

  &:hover {
    border-color: var(--primary-color, #001c74);
  }

  &.active {
    border-color: var(--primary-color, #001c74);
    background: color-mix(
      in srgb,
      var(--primary-color, #001c74),
      transparent 88%
    );
    color: var(--primary-color, #001c74);
  }
}

.field-pill-label {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.field-pill-check {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1.15rem;
  height: 1.15rem;
  flex-shrink: 0;
  border-radius: 50%;
  border: 1.5px solid rgba(0, 0, 0, 0.4);
  background: rgba(255, 255, 255, 0.65);
  transition:
    background-color 0.15s ease,
    border-color 0.15s ease;

  svg {
    width: 0.8rem;
    height: 0.8rem;
    color: #fff;
  }

  .field-pill.active & {
    background: var(--primary-color, #001c74);
    border-color: var(--primary-color, #001c74);
  }
}

.actions {
  justify-content: flex-end;
  gap: 1rem;
}

.status-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.status-chip {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  border: 1px solid #cfd3dc;
  border-radius: 999px;
  padding: 0.38rem 0.55rem 0.38rem 0.85rem;
  font-size: 0.9rem;
  line-height: 1.2;
  font-weight: 600;
  background: #fff;
  color: #374151;
  cursor: pointer;
  transition:
    background-color 0.15s ease,
    border-color 0.15s ease;

  &:hover {
    border-color: var(--primary-color, #001c74);
  }

  &.active {
    font-weight: 700;
    color: #1f2937;
    background-color: var(--chip-color);
    border-color: color-mix(in srgb, var(--chip-color), black 22%);
  }
  &.undercertificate {
    --chip-color: var(--invoice-undercertificate);
  }
  &.parked {
    --chip-color: var(--invoice-parked);
  }
  &.disputed {
    --chip-color: var(--invoice-disputed);
  }
  &.voided {
    --chip-color: var(--invoice-voided);
  }
  &.approved {
    --chip-color: var(--invoice-approved);
  }
  &.paid {
    --chip-color: var(--invoice-paid);
  }
  &.verified-debt {
    --chip-color: var(--invoice-verified-debt);
  }
}

.vat-type-chip {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  border: 1px solid #cfd3dc;
  border-radius: 999px;
  padding: 0.38rem 0.55rem 0.38rem 0.85rem;
  font-size: 0.9rem;
  line-height: 1.2;
  font-weight: 600;
  background: #fff;
  color: #374151;
  cursor: pointer;
  transition:
    background-color 0.15s ease,
    border-color 0.15s ease,
    color 0.15s ease;

  &:hover {
    border-color: var(--primary-color, #001c74);
  }

  &.active {
    border-color: var(--primary-color, #001c74);
    background: color-mix(
      in srgb,
      var(--primary-color, #001c74),
      transparent 88%
    );
    color: var(--primary-color, #001c74);
  }

  &.active .status-chip-check {
    background: var(--primary-color, #001c74);
    border-color: var(--primary-color, #001c74);
  }
}

.status-chip-label {
  line-height: 1.2;
}

.status-chip-check {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1.15rem;
  height: 1.15rem;
  flex-shrink: 0;
  border-radius: 50%;
  border: 1.5px solid rgba(0, 0, 0, 0.4);
  background: rgba(255, 255, 255, 0.65);
  transition:
    background-color 0.15s ease,
    border-color 0.15s ease;

  svg {
    width: 0.8rem;
    height: 0.8rem;
    color: #fff;
  }

  .status-chip.active & {
    background: color-mix(in srgb, var(--chip-color), black 38%);
    border-color: color-mix(in srgb, var(--chip-color), black 38%);
  }
}

.date-range {
  display: flex;
  gap: 1rem;
  margin-top: 1rem;

  .field {
    flex: 1 1 0;
    min-width: 0;
  }

  .label {
    font-weight: 700;
    font-size: 0.85rem;
  }
}

.message-no-matches {
  color: #b7791f;
  font-weight: 600;
}

.message-error {
  color: #c53030;
  font-weight: 600;
}

.export-spinner {
  display: inline-block;
  width: 1rem;
  height: 1rem;
  margin-right: 0.5rem;
  border: 2px solid rgba(255, 255, 255, 0.5);
  border-top-color: #fff;
  border-radius: 50%;
  animation: export-spin 0.7s linear infinite;
  vertical-align: middle;
}

@keyframes export-spin {
  to {
    transform: rotate(360deg);
  }
}

@media (max-width: 720px) {
  .export-columns {
    flex-direction: column;
    gap: 1.25rem;
  }
}

@media (max-width: 600px) {
  .date-range {
    flex-direction: column;
  }
}
</style>
